import { Injectable, EventEmitter, signal } from '@angular/core';
import { PopupData } from '../model/popup-data';
import { saveAs } from 'file-saver';
import { ActivatedRoute, Router } from '@angular/router';
import { ColumnSearchDetails } from '../model/bulk-operations';
import { ScopeService } from './scope.service';
import { GlobalVariables } from '../constants/global-constants';
import { hasDecisionPageAccess } from '../constants/decision-page-access';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FdSnackbarComponent } from '../components/shared/fd-snackbar/fd-snackbar.component';
import { FdTableImportPopupComponent } from '../components/shared/fd-table-import-popup/fd-table-import-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UtilitiesService {
  private fullPage = false;
  private fdClientId = 0;
  private menuItem = structuredClone(GlobalVariables.menu);
  private scope = {};
  private resourceRoute = GlobalVariables.ResourceURL;
  fullPageEvent = new EventEmitter<any>();
  fullPageLoadingEvent = new EventEmitter<any>();
  displayLogoEvent = new EventEmitter<any>();
  filteredMenuItem;
  isDarkTheme = signal<boolean>(true);
  isSelectClientRoute = signal<boolean>(false);
  isOnboardingRoute = signal<boolean>(false);
  isGlobalRoute = signal<boolean>(false);

  private _onboardingSplash$ = new BehaviorSubject<{ show: boolean }>({ show: false });
  onboardingSplash$ = this._onboardingSplash$.asObservable();

  /** Signals widgets like the header to resync selected-client display without a full route reload. */
  private readonly clientContextRefresh = new Subject<void>();
  clientContextRefresh$ = this.clientContextRefresh.asObservable();

  notifyClientContextRefresh(): void {
    this.clientContextRefresh.next();
  }


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private scopeService: ScopeService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,

  ) { }

  set menuList(menu) {
    this.menuItem = menu;
  }

  get menuList() {
    return this.menuItem;
  }

  set fullPageLoading(isLoading) {
    this.fullPageLoadingEvent.emit(isLoading);
  }

  get isfullPage() {
    return this.fullPage;
  }

  set isfullPage(fullpage: boolean) {
    this.fullPage = fullpage;
    this.fullPageEvent.emit(this.fullPage);
  }

  set showLogo(show) {
    this.displayLogoEvent.emit(show);
  }

  set clientId(id) {
    this.fdClientId = id;
  }
  get clientId() {
    var urlParam = this.route.params['clientId'];
    var id = this.fdClientId;
    if (!id && urlParam) {
      this.fdClientId = id = urlParam;
    }
    return id;
  }


  notifPopup(data: PopupData) {
    this.showSnackbarAlert(data);
  }

  export(data, formattedHeader, tableName = '') {
    const replacer = (key, value) =>
      key != null && value === null ? '' : value;
    var header = Object.keys(data[0]);
    var data = data;
    let csv = data.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(',')
    );
    csv.unshift(
      header
        .map((x) => {
          return formattedHeader[x] ? formattedHeader[x] : x;
        })
        .join(',')
    );
    let csvArray = csv.join('\r\n');

    var blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, (tableName ? tableName + '_' : '') + 'export_data.csv');
  }

  downloadSampleCsv(data, formattedHeader, tableName = '') {
    const replacer = (key, value) =>
      key != null && value === null ? '' : value;
    var header = Object.keys(data[0]);
    var data = data;
    let csv = data.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(',')
    );
    csv.unshift(
      header
        .map((x) => {
          return formattedHeader[x] ? formattedHeader[x] : x;
        })
        .join(',')
    );
    let csvArray = csv.join('\r\n');

    var blob = new Blob([csvArray], { type: 'text/csv' });
    saveAs(blob, (tableName ? tableName + '_' : '') + 'sample_import_file.csv');
  }

  import(data) {
    const dialogRef = this.dialog.open(FdTableImportPopupComponent, {
      height: '90%',
      minHeight: '400px',
      width: '50%',
      data: data,
      disableClose: true,
      autoFocus: false,
    });
    return dialogRef;
  }

  //This is for building the dynamic query params for search per columns in tables
  getColumnSearchParams(
    columnName,
    columnValue,
    columnSearches
  ): ColumnSearchDetails {
    columnName = columnName.replace('searchInput_', '');
    let existingColumnIndex = -1;
    let columnSearchQueryParams = '';
    let columnSearchDetails: ColumnSearchDetails;

    //Checking if the item is already added on the dynamic query params holder
    existingColumnIndex = columnSearches.findIndex(
      (x) => x.columnName == columnName
    );

    if (existingColumnIndex != -1) {
      //If existing, then just update the column value(search value)
      columnSearches[existingColumnIndex].columnValue = columnValue;
    } else {
      //If not existing, then add a new entry on the holder
      columnSearches.push({
        columnName: columnName,
        columnValue: columnValue,
      });
    }

    columnSearches.forEach((columnSearch) => {
      //Add a check to remove the empty column name or column value in the query params being sent to the API server
      if (columnSearch.columnName != '' && columnSearch.columnValue != '') {
        columnSearchQueryParams =
          columnSearchQueryParams +
          '&' +
          columnSearch.columnName +
          '=' +
          columnSearch.columnValue;
      }
    });

    columnSearchDetails = {
      columnSearches: columnSearches,
      columnSearchQueryParams: columnSearchQueryParams,
    };

    return columnSearchDetails;
  }


  removeQueryParam(
    columnSearchDetails: ColumnSearchDetails,
    columnName: string
  ): ColumnSearchDetails {
    columnName = columnName.replace('searchInput_', '');

    const updatedColumnSearches = columnSearchDetails.columnSearches.filter(
      (column) => column.columnName !== columnName
    );

    const updatedQueryParams = updatedColumnSearches
      .filter(
        (column) => column.columnName && column.columnValue
      )
      .map(
        (column) => `${column.columnName}=${column.columnValue}`
      )
      .join('&');

    return {
      columnSearches: updatedColumnSearches,
      columnSearchQueryParams: updatedQueryParams ? `&${updatedQueryParams}` : '',
    };
  }

  getColumnSearchParamsMultiselect(
    columnName: string,
    columnValue: string | string[],
    columnSearches: any[]
  ): ColumnSearchDetails {
    let existingColumnIndex = columnSearches.findIndex(x => x.columnName === columnName);

    if (existingColumnIndex !== -1) {
      let existingValue = columnSearches[existingColumnIndex].columnValue;

      let existingArray = typeof existingValue === 'string'
        ? existingValue.split('|')
        : Array.isArray(existingValue)
          ? existingValue
          : [];

      let newValues = Array.isArray(columnValue) ? columnValue : [columnValue];

      let updatedValues = [...new Set([...existingArray, ...newValues])];

      columnSearches[existingColumnIndex].columnValue = updatedValues.join('|');
    } else {
      columnSearches.push({
        columnName: columnName,
        columnValue: Array.isArray(columnValue) ? columnValue.join('|') : columnValue
      });
    }

    const columnSearchQueryParams = columnSearches
      .filter(column => column.columnName && column.columnValue.length)
      .map(column => `${column.columnName}=${column.columnValue}`)
      .join('&');

    return {
      columnSearches: columnSearches,
      columnSearchQueryParams: columnSearchQueryParams ? `&${columnSearchQueryParams}` : '',
    };
  }



  removeQueryParamMultiselect(
    columnSearchDetails: ColumnSearchDetails,
    columnName: string,
    valueToRemove: string
  ): ColumnSearchDetails {

    const updatedColumnSearches = columnSearchDetails.columnSearches
      .map(column => {
        if (column.columnName === columnName) {
          const values = typeof column.columnValue === 'string'
            ? column.columnValue.split('|')
            : Array.isArray(column.columnValue)
              ? column.columnValue
              : [];
          const updatedValues = values.filter(val => val !== valueToRemove);

          const updatedColumnValue = Array.isArray(updatedValues)
            ? updatedValues.join('|')
            : updatedValues;

          return { ...column, columnValue: updatedColumnValue };
        }
        return column;
      })
      .filter(column => column.columnValue.length > 0);

    const updatedQueryParams = updatedColumnSearches
      .map(column => `${column.columnName}=${column.columnValue}`)
      .join('&');

    return {
      columnSearches: updatedColumnSearches,
      columnSearchQueryParams: updatedQueryParams ? `&${updatedQueryParams}` : '',
    };
  }


  formatDate(date: Date): string {
    return date
      .toISOString()
      .replace('T', ' ')
      .replaceAll('-', '/')
      .substring(0, 19)
      .split(' ')[0];
  }

  formatStringDate(date: string): string {
    return date
      .replace('T', ' ')
      .replaceAll('-', '/')
      .substring(0, 19)
      .split(' ')[0];
  }

  getPreviousMonthDate(currDate: Date): Date {
    return new Date(
      currDate.getFullYear(),
      currDate.getMonth() - 1,
      currDate.getDate()
    );
  }

  dateRange(startDate, endDate, steps = 1) {
    const dateArray = [];
    let currentDate = new Date(startDate);

    while (currentDate <= new Date(endDate)) {
      dateArray.push(this.formatDate(new Date(currentDate)));
      // Use UTC date to prevent problems with time zones and DST
      currentDate.setUTCDate(currentDate.getUTCDate() + steps);
    }

    return dateArray;
  }

  convertToDate(str) {
    var date = new Date(str),
      mnth = ('0' + (date.getMonth() + 1)).slice(-2),
      day = ('0' + date.getDate()).slice(-2);
    var formattedDate = [date.getFullYear(), mnth, day].join('-');
    return formattedDate;
  }

  scopeMenu() {
    var Tables = $localize`Tables`;
    var Admin = $localize`Admin`;
    var Queue = $localize`Queue`;
    var QuicksightDashboard = 'Dashboard';
    var Titan = 'Titan';
    var Tools = $localize`Tools`;
    var FDID = 'FD.ID';
    var Calculator = $localize`Savings Calculator`;
    var Onboarding = 'Onboarding'

    this.scope = this.scopeService.scope;
    this.filteredMenuItem = structuredClone(GlobalVariables.menu);
    this.menuItem = structuredClone(GlobalVariables.menu)
    if (this.scope) {
      this.filteredMenuItem = this.menuItem.filter(
        (item) =>
          this.scopeService
            .resourceScope(
              item.scope?.replace('/', '').toLowerCase() ??
              item.url?.replace('/', '').toLowerCase()
            )?.[0]
            ?.actions?.includes('read') ||
          item.name == Tables ||
          item.name == Queue ||
          item.name == Admin ||
          item.name == QuicksightDashboard ||
          item.name == Titan ||
          item.name == Tools ||
          item.name == FDID ||
          item.name == Onboarding

      );
      //filter queue resource
      if (
        this.filteredMenuItem.filter((x) => x.name == Queue && x.subMenu.length > 0)
          .length > 0
      )
        this.filteredMenuItem.filter(
          (x) => x.name == Queue && x.subMenu.length > 0
        )[0].subMenu = this.filteredMenuItem
          .filter((x) => x.name == Queue && x.subMenu.length > 0)[0]
          .subMenu.filter((table) =>
            this.scopeService
              .resourceScope(
                this.resourceRoute[
                table.scope.replace('queue/', '').toLowerCase()
                ]
              )?.[0]
              ?.actions?.includes('read')
          );
      //filter table resource
      if (
        this.filteredMenuItem.filter((x) => x.name == Tables && x.subMenu.length > 0)
          .length > 0
      )
        this.filteredMenuItem.filter(
          (x) => x.name == Tables && x.subMenu.length > 0
        )[0].subMenu = this.filteredMenuItem
          .filter((x) => x.name == Tables)[0]
          .subMenu.filter((table) =>
            this.scopeService
              .resourceScope(
                this.resourceRoute[table.scope.replace('/', '').toLowerCase()]
              )?.[0]
              ?.actions?.includes('read')
          );
      //filter titan resource
      if (
        this.filteredMenuItem.filter((x) => x.name == Titan && x.subMenu.length > 0)
          .length > 0
      )

        this.filteredMenuItem.filter(
          (x) => x.name == Titan && x.subMenu.length > 0

        )[0].subMenu = this.filteredMenuItem
          .filter((x) => x.name == Titan)[0]
          .subMenu.filter((titan) =>
            this.scopeService
              .resourceScope(
                this.resourceRoute[titan.scope.replace('/', '').toLowerCase()]
              )?.[0]
              ?.actions?.includes('read')
          );
      //filter admin resource
      const adminMenu = this.filteredMenuItem.find((x) => x.name == Admin);
      if (adminMenu) {
        if (this.roleValue === 'partner_onboarding_user') {
          // partner_onboarding_user only sees Create New Merchant
          adminMenu.subMenu = adminMenu.subMenu.filter(
            (resource) => resource.url === 'admin/decision'
          );
        } else if(this.isAcquirerUserValue) {
          adminMenu.subMenu = adminMenu.subMenu.filter((resource) => {
            if (resource.url === 'admin/decision') {
              return true;
            }
            return this.scopeService
              .resourceScope(
                this.resourceRoute[resource.scope.replace('/', '').toLowerCase()]
              )?.[0]
              ?.actions?.includes('read');
          });
        } else {
          adminMenu.subMenu = adminMenu.subMenu.filter((resource) => {
            // Create New Merchant is restricted to FD users, system_admin, partner_onboarding_user, acquirer_admin , and demo users 
            if (resource.url === 'admin/decision') {
              return this.isFdUserValue || this.isDemoValue;
            }
            return this.scopeService
              .resourceScope(
                this.resourceRoute[resource.scope.replace('/', '').toLowerCase()]
              )?.[0]
              ?.actions?.includes('read');
          });
        }

        const merchantManagementItem = adminMenu.subMenu.find(
          (resource) => resource.url === 'admin/decision'
        );
        if (
          merchantManagementItem &&
          !hasDecisionPageAccess(this.clientId, this.isFdUserValue)
        ) {
          merchantManagementItem.url = 'admin/single-merchant';
        }
      }
      //filter tools resource
      if (
        this.filteredMenuItem.filter((x) => x.name == Tools && x.subMenu.length > 0)
          .length > 0
      ) {
        this.filteredMenuItem.find(
          (x) => x.name == Tools && x.subMenu.length > 0).subMenu = this.filteredMenuItem
            .find((x) => x.name == Tools)
            .subMenu.filter((tool) =>
              this.scopeService
                .resourceScope(
                  tool.scope
                )?.[0]
                ?.actions?.includes('read')
            );
      }
      //filter fdId resource
      if (
        this.filteredMenuItem.filter((x) => x.name == FDID && x.subMenu.length > 0)
          .length > 0
      ) {
        this.filteredMenuItem.find(
          (x) => x.name == FDID && x.subMenu.length > 0).subMenu = this.filteredMenuItem
            .find((x) => x.name == FDID)
            .subMenu.filter((fdid) =>
              this.scopeService
                .resourceScope(
                  fdid.scope
                )?.[0]
                ?.actions?.includes('read')
            );
      }
      if (
        this.filteredMenuItem.filter((x) => x.name == Onboarding && x.subMenu.length > 0)
          .length > 0
      ) {
        this.filteredMenuItem.find(
          (x) => x.name === Onboarding && x.subMenu.length > 0).subMenu = this.filteredMenuItem
            .find((x) => x.name === Onboarding)
            .subMenu.filter((Onboarding) =>
              this.scopeService
                .resourceScope(
                  Onboarding.scope
                )?.[0]
                ?.actions?.includes('read')
            );
      }
      this.filteredMenuItem = this.filteredMenuItem.filter(
        (x) =>
          (x.name != Tables && x.name != Queue && x.name != Admin && x.name != Titan && x.name != Tools && x.name != FDID && x.name != Onboarding) ||
          ((x.name == Tables || x.name == Queue || x.name == Admin || x.name == Titan || x.name == Tools || x.name == FDID || x.name == Onboarding) &&
            x.subMenu.length > 0)
      );
      //filter quicksight resource
      const isDemo = this.isDemoValue;
      const isPartner = this.isPartnerValue;
      const parnerDashboardLabel = ['Partner Dashboard', 'Dashboard do Parceiro', 'Dashboard del Socio', 'Partnera Dashboard'];
      const hasQuickSightPermission = this.scopeService.resourceScope('quicksight')?.[0]?.actions?.includes('read');
      const hasSavingsPermission = this.scopeService.resourceScope('savings-calculator')?.[0]?.actions?.includes('read');

      this.filteredMenuItem = this.filteredMenuItem.filter(item => {
        if (!hasQuickSightPermission) {
          if (item.name === 'Dashboard' || parnerDashboardLabel.includes(item.name)) {
            return false;
          }
        } else if (isPartner) {
          if (item.name === 'Dashboard') {
            return false;
          }
          if(this.roleValue === 'acquirer_user' && parnerDashboardLabel.includes(item.name)) {
            return false;
          }
        } else {
          if (parnerDashboardLabel.includes(item.name)) {
            return false;
          }
        }
        if (item.name === Calculator) {
          if (isDemo) {
            return hasSavingsPermission;
          } else {
            return hasSavingsPermission;
          }
        }
        return true;
      });
    }
  }


  get defaultURL() {
    this.scopeMenu();
    if (this.isPartnerValue) {
      if(this.roleValue === 'acquirer_user') {
        return 'queue/alerts'
      }
      return 'quicksight/partner-dashboard';
    }
    if (this.menuList.length > 0) {
      var first = this.menuList[0];
      if (first.subMenu.length > 0) {
        return first.subMenu[0].url;
      } else {
        return first.url;
      }
    } else {
      return '';
    }
  }

  redirectToDefaultURL() {
    var url = this.defaultURL;
    var clientId = this.clientId;

    if (url === '') {
      this.router.navigate(['client', 'user-profile']);
    }
    else if (url.split('/').length === 2) {
      var urlArr = url.split('/');
      this.router.navigate(['client', clientId, urlArr[0], urlArr[1]]);
    }
    else {
      this.router.navigate(['client', clientId, url.replace('/', '')]);
    }
  }

  redirectToContractAcknowledgment(fdClientId?: number) {
    const extras = fdClientId ? { queryParams: { fdcid: fdClientId } } : {};
    this.router.navigate(['/contract-acknowledgment'], extras);
  }

  isNotNullUndefinedOrEmpty(value) {
    return value !== null && value !== undefined && value !== '';
  }

  showSnackbarAlert(popupdata: PopupData) {
    this.snackBar.openFromComponent(FdSnackbarComponent, {
      duration: GlobalVariables.snackBarDuration,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: popupdata.warn ? 'fd-snackbar-failed' : 'fd-snackbar-success',
      data: popupdata,
    });
  }

  decimalFormatValidator(
    digitLength: number,
    decimalLength: number
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // var regex = /^(?!$)\d{0,10}(?:\.\d{1,2})?$/g;

      var pattern = `^-?(?!$)\\d{0,${digitLength}}(?:\\.\\d{1,${decimalLength}})?$`;
      var regex = new RegExp(pattern, 'g');
      return regex.test(control.value) || !control.value
        ? null
        : { decimalFormat: true };
    };
  }

  setTheme(theme: string) {

    var currentTheme = localStorage.getItem('theme');
    theme = theme.length > 0 ? theme : currentTheme;
    currentTheme = theme;
    if (!currentTheme) {
      currentTheme = GlobalVariables.lightTheme;
    }

    if (currentTheme == GlobalVariables.darkTheme) {
      document.body.classList.remove(GlobalVariables.lightTheme);
      document.body.classList.add(GlobalVariables.darkTheme);
    }
    else {
      document.body.classList.remove(GlobalVariables.darkTheme);
      document.body.classList.add(GlobalVariables.lightTheme);
    }
  }

  updateRouteStatus(isSelectClient: boolean): void {
    this.isSelectClientRoute.set(isSelectClient);
  }

  updateRouteOnboardingStatus(isOnboarding: boolean): void {
    this.isOnboardingRoute.set(isOnboarding);
  }

  updateRouteGlobalStatus(isGlobal: boolean): void {
    this.isGlobalRoute.set(isGlobal);
  }


  clearQueryParams(): void {
    this.router.navigate([], {
      queryParams: {},
      replaceUrl: true
    });
  }
  private companyStatusSubject = new BehaviorSubject<string | null>(null);
  companyStatus$ = this.companyStatusSubject.asObservable();


  private companyNameSubject = new BehaviorSubject<string | null>(null);
  companyName$ = this.companyNameSubject.asObservable();

  private isPartnerSubject = new BehaviorSubject<boolean>(false);
  isPartner$ = this.isPartnerSubject.asObservable();

  private isUniqueClientSubject = new BehaviorSubject<boolean>(false);
  isUniqueClient$ = this.isUniqueClientSubject.asObservable();

  private roleSubject = new BehaviorSubject<string | null>(null);
  role$ = this.roleSubject.asObservable();

  private userEmailSubject = new BehaviorSubject<string | null>(null);
  userEmail$ = this.userEmailSubject.asObservable();

  private isDemoSubject = new BehaviorSubject<boolean>(false);
  isDemo$ = this.isDemoSubject.asObservable();


  private isFdUserSubject = new BehaviorSubject<boolean>(false);
  isFdUserSubject$ = this.isFdUserSubject.asObservable();

  private isDemoOnbSubject = new BehaviorSubject<boolean>(false);
  isisDemoOnbSubject$ = this.isDemoOnbSubject.asObservable();

  private isNexioClientSubject = new BehaviorSubject<boolean>(false);
  isNexioClient$ = this.isNexioClientSubject.asObservable();

  private merchantOnboardingIdSubject = new BehaviorSubject<number | null>(null);
  merchantOnboardingId$ = this.merchantOnboardingIdSubject.asObservable();

  private isAcquirerUserSubject = new BehaviorSubject<boolean>(false);
  isAcquirerUser$ = this.isAcquirerUserSubject.asObservable();

  setMerchantOnboardingId(id: number) {
    this.merchantOnboardingIdSubject.next(id);
  }

  setCompanyStatus(status: string) {
    this.companyStatusSubject.next(status);
  }

  setCompanyName(name: string) {
    this.companyNameSubject.next(name);
  }

  setIsPartner(isPartner: boolean) {
    this.isPartnerSubject.next(isPartner);
  }

  setIsUniqueClient(isUnique: boolean) {
    this.isUniqueClientSubject.next(isUnique);
  }

  setRole(role: string) {
    this.roleSubject.next(role);
  }

  setUserEmail(email: string) {
    this.userEmailSubject.next(email);
  }

  setIsDemo(isDemo: boolean) {
    this.isDemoSubject.next(isDemo);
  }

  setIsFdUser(isFdUser: boolean) {
    this.isFdUserSubject.next(isFdUser);
  }

  setIsDemoOnb(isDemoOnb: boolean) {
    this.isDemoOnbSubject.next(isDemoOnb);
  }

  setIsNexioClient(isNexio: boolean) {
    this.isNexioClientSubject.next(isNexio);
  }
  
  setIsAcquirerUser(isAcquirerUser: boolean) {
    this.isAcquirerUserSubject.next(isAcquirerUser);
  }

  get isPartnerValue() {
    return this.isPartnerSubject.getValue();
  }

  get companyNameValue() {
    return this.companyNameSubject.getValue();
  }

  get roleValue() {
    return this.roleSubject.getValue();
  }

  get isAcquirerUserValue() {
    return this.isAcquirerUserSubject.getValue();
  }

  get isUniqueClientValue() {
    return this.isUniqueClientSubject.getValue();
  }

  get userEmailValue() {
    return this.userEmailSubject.getValue();
  }

  get isDemoValue() {
    return this.isDemoSubject.getValue();
  }

  get isFdUserValue() {
    return this.isFdUserSubject.getValue();
  }

  get companyStatusValue() {
    return this.companyStatusSubject.getValue();
  }

  get isDemoOnbValue() {
    return this.isDemoOnbSubject.getValue();
  }

  get isNexioClientValue() {
    return this.isNexioClientSubject.getValue();
  }

  get isPartnerOnboardingUser(): boolean {
    const role = this.roleValue;
    return role === 'partner_onboarding_user' || role === 'merchant_admin';
  }

  get merchantOnboardingIdValue() {
    return this.merchantOnboardingIdSubject.getValue();
  }

  clearUserContext() {
    this.companyNameSubject.next(null);
    this.isPartnerSubject.next(false);
    this.isUniqueClientSubject.next(false);
    this.roleSubject.next(null);
    this.userEmailSubject.next(null);
    this.merchantOnboardingIdSubject.next(null);
  }

  public getConnectionMessage(statusCode: number): string {
    switch (statusCode) {
      case 200:
        return 'Connection tested successfully.';
      case 400:
        return 'Bad request. Please review the data.';
      case 401:
        return 'Could not authenticate credentials.';
      case 403:
        return 'Forbidden access. Check permissions.';
      case 404:
        return 'Gateway not found.';
      case 408:
        return 'Request timeout.';
      case 500:
        return 'Server error at gateway.';
      case 502:
        return 'Bad gateway.';
      case 503:
        return 'Service unavailable.';
      case 504:
        return 'Gateway timeout.';
      default:
        return `Unexpected response (${statusCode}).`;
    }
  }

  showOnboardingSplash() {
    this._onboardingSplash$.next({ show: true });
  }

  hideOnboardingSplash() {
    this._onboardingSplash$.next({ show: false });
  }

  goToOnboarding() {
    this.hideOnboardingSplash();
    this.router.navigate(['/client-onboarding']);
  }

}
